/**
 * rememberMe — T313. His words: *"bring remember me for id and pass word , when new id is
 * entered promt to ask remember the id and password"*.
 *
 * ⚠ THE ID AND THE PASSWORD ARE NOT THE SAME KIND OF SECRET, so they are not stored the same way.
 *
 *   · The ID is not a secret at all. It goes in localStorage on every platform.
 *   · The PASSWORD is only ever kept where the OPERATING SYSTEM can encrypt it — the desktop
 *     shell's safeStorage (DPAPI on Windows, Keychain on macOS), the same vault the auth token
 *     already lives in. In a BROWSER there is no such vault: localStorage is plain text that any
 *     script on the origin can read, so on web we remember the ID and refuse the password. That
 *     is not a missing feature; a password box that quietly writes plain text is the bug.
 *
 * A user on web is not left retyping a long password: the PIN (T314) is the fast path, and it is
 * a server-side secret the device never holds.
 */

import { isDesktop, platformBridge } from './platformBridge';

const ID_KEY = 'forge_remembered_id';
const PW_KEY = 'forge_remembered_pw';
const ASKED_KEY = 'forge_remember_asked';

/** True only where the OS will encrypt what we store — see the header. */
export const canRememberPassword: boolean = isDesktop;

function readPlain(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writePlain(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* private mode / quota */ }
}
function dropPlain(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

export function getRememberedId(): string | null {
  const id = readPlain(ID_KEY);
  return id && id.trim() ? id.trim() : null;
}

export function rememberId(identifier: string): void {
  if (identifier && identifier.trim()) writePlain(ID_KEY, identifier.trim());
}

/**
 * Store the password for `identifier`, encrypted by the OS. The identifier is stored WITH it so a
 * password can never be handed back for a different ID than the one it was saved under.
 */
export async function rememberPassword(identifier: string, password: string): Promise<void> {
  if (!canRememberPassword || !identifier || !password) return;
  try {
    await platformBridge.secureStore.set(PW_KEY, JSON.stringify({ id: identifier.trim(), password }));
  } catch { /* an unavailable vault means we simply do not remember it */ }
}

/** The saved password, but ONLY if it belongs to the ID being asked about. */
export async function getRememberedPassword(identifier: string): Promise<string | null> {
  if (!canRememberPassword || !identifier) return null;
  try {
    const raw = await platformBridge.secureStore.get(PW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.id !== identifier.trim()) return null;
    return typeof parsed.password === 'string' ? parsed.password : null;
  } catch {
    return null;
  }
}

/** Drop everything remembered about the person — used by "Not me" and by a failed sign-in. */
export async function forgetIdentity(): Promise<void> {
  dropPlain(ID_KEY);
  dropPlain(ASKED_KEY);
  dropPlain(PIN_LEN_KEY);
  try { await platformBridge.secureStore.delete(PW_KEY); } catch { /* ignore */ }
}

/* ── the prompt is per ID, and asked ONCE ─────────────────────────────────────────────────────
   *"when new id is entered promt to ask"* — a prompt that returns every single sign-in is a
   prompt people learn to dismiss without reading, so a "Not now" is remembered too. */
function askedList(): string[] {
  try { return JSON.parse(readPlain(ASKED_KEY) || '[]'); } catch { return []; }
}

export function hasBeenAsked(identifier: string): boolean {
  return askedList().includes((identifier || '').trim());
}

export function markAsked(identifier: string): void {
  const id = (identifier || '').trim();
  if (!id) return;
  const list = askedList().filter(x => x !== id);
  list.push(id);
  writePlain(ASKED_KEY, JSON.stringify(list.slice(-10)));
}

/* ── which remembered IDs have a PIN ──────────────────────────────────────────────────────────
   The login screen must not offer a PIN box to someone who has never set one, and it cannot ask
   the server — "does this ID have a PIN?" is a question an unauthenticated caller may not ask,
   because answering it tells a stranger which IDs exist. So the DEVICE remembers, written by the
   Settings card that sets or removes the PIN and by a successful PIN sign-in. It is a hint about
   this device only; the server still decides every actual sign-in. */
const PIN_IDS_KEY = 'forge_pin_ids';

function pinIds(): string[] {
  try { return JSON.parse(readPlain(PIN_IDS_KEY) || '[]'); } catch { return []; }
}

export function hasPinHint(identifier: string | null): boolean {
  if (!identifier) return false;
  return pinIds().includes(identifier.trim());
}

export function setPinHint(identifier: string | null, has: boolean): void {
  const id = (identifier || '').trim();
  if (!id) return;
  const list = pinIds().filter(x => x !== id);
  if (has) list.push(id);
  writePlain(PIN_IDS_KEY, JSON.stringify(list.slice(-10)));
  if (!has) dropPlain(PIN_LEN_KEY);
}

/* ── T326: how LONG the PIN is, so the boxes match it and it can submit itself ──────────────────
   His words: *"when I enter the four digit number, it opened the next digit to type ... when the
   correct password/pin is typed, it should auto log in"*.

   ⚠ THE LENGTH IS NOT THE PIN, and it is the only part of it a device may hold. It buys two
   things: the boxes are the RIGHT NUMBER of boxes — a fifth one opening after a four-digit PIN is
   what he saw — and the form knows when the entry is COMPLETE, which is the closest an offline
   client can get to "correct". The server still decides. Without the hint we fall back to 4, the
   shortest a PIN may be, and the button is still there. */
const PIN_LEN_KEY = 'forge_pin_len';

export function setPinLengthHint(length: number): void {
  if (length >= 4 && length <= 8) writePlain(PIN_LEN_KEY, String(length));
}

export function getPinLengthHint(): number | null {
  const raw = Number(readPlain(PIN_LEN_KEY));
  return raw >= 4 && raw <= 8 ? raw : null;
}

/* ── T318: the method he used LAST ────────────────────────────────────────────────────────────
   His words: *"whichever user select at last that should be retained"*. The sign-in screen opens
   on the door he last came through, per device. It is a PREFERENCE, not a permission — the
   server decides every sign-in either way. */
export type SignInMethod = 'password' | 'pin';
const METHOD_KEY = 'forge_last_signin_method';

export function getLastMethod(): SignInMethod | null {
  const value = readPlain(METHOD_KEY);
  return value === 'pin' || value === 'password' ? value : null;
}

export function setLastMethod(method: SignInMethod): void {
  writePlain(METHOD_KEY, method);
}
